import { Button, Spinner, Tooltip } from '@heroui/react';
import React, { useState } from 'react';
import { FiCheck, FiCopy, FiKey, FiRefreshCw } from 'react-icons/fi';

import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import { useSupportCode } from '../hooks/useSupportCode';

/**
 * The code is almost always pasted straight into a login page, so hovering
 * shows it and clicking copies it: no popover to open and select by hand.
 * Both check the code first. An expired code is never shown or copied: the
 * button turns into a refresh button instead.
 */
export const SupportCodeButton: React.FC = () => {
  const { supportCode, getCurrentCode } = useSupportCode();
  const { copy, justCopied } = useCopyToClipboard('code of the day');
  // Tooltips close on press. Reopen it so "Copied" is visible after a click.
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const isExpired = supportCode.status === 'expired';

  const handleOpenChange = (isOpen: boolean) => {
    setIsTooltipOpen(isOpen);
    if (isOpen) {
      getCurrentCode();
    }
  };

  const handlePress = async () => {
    setIsTooltipOpen(true);
    const { code } = await getCurrentCode();
    // Refreshing shows the new code; copying it waits for the next click.
    if (!isExpired) {
      await copy(code);
    }
  };

  const hint = (() => {
    if (isExpired) return 'Click to refresh';
    return justCopied ? 'Copied' : 'Click to copy';
  })();

  return (
    <Tooltip
      isOpen={isTooltipOpen}
      onOpenChange={handleOpenChange}
      placement="bottom-end"
      showArrow
      content={
        <div className="flex flex-col items-center gap-0.5 px-1 py-1">
          <span className="text-xs text-default-500">Code of the day</span>
          <span
            className="text-base font-bold tracking-wider text-foreground"
            data-testid="support-code-value"
          >
            {supportCode.status === 'current' && supportCode.code}
            {supportCode.status === 'refreshing' && <Spinner size="sm" />}
            {supportCode.status === 'expired' && 'Code expired'}
          </span>
          <span className="text-xs text-default-500">{hint}</span>
        </div>
      }
    >
      <Button
        isIconOnly
        variant="light"
        size="sm"
        onPress={handlePress}
        aria-label={
          isExpired ? 'Refresh code of the day' : 'Copy code of the day'
        }
        data-testid="support-code-button"
        className="group text-primary-600 dark:text-primary-300 hover:bg-primary-100/60 dark:hover:bg-primary-400/20"
      >
        {/* Explicit size + inherited currentColor so the glyph stays
            visible on the header gradient in both themes */}
        {justCopied && <FiCheck size={18} className="text-success" />}
        {!justCopied && isExpired && <FiRefreshCw size={18} />}
        {!justCopied && !isExpired && (
          <>
            <FiKey
              size={18}
              className="group-hover:hidden group-data-[focus-visible=true]:hidden"
            />
            <FiCopy
              size={18}
              className="hidden group-hover:block group-data-[focus-visible=true]:block"
            />
          </>
        )}
      </Button>
    </Tooltip>
  );
};
